import React, { useEffect, useState, FunctionComponent } from "react";
import {
  styled,
  Block,
  Button,
  ListItemBody,
  ListItemRow,
  ListItemCell,
  ListItemContent,
  Paper,
  List,
  EmptyEntityFallbackListItem,
} from "../../../seenspire-library";
import { IRouted } from "../../routes";
import { Container } from "../../components/Container/Container";
import Search from "../../components/Search/Search";
import EmptyTable from "../../components/EmptyTable/EmptyTable";
import { debounce, chain, size, get } from "lodash";
import { Avatar } from "../../components/Avatar/Avatar";
import { useToggle } from "react-use";
import InviteUserModal from "../../modals/InviteUserModal";
import { name, getCurrentRoles, dt } from "../../util";
import { find, last, isEqual } from "lodash";
import ConfirmationModal from "../../modals/ConfirmationModal";
import { Layout } from "../../components";
import EditUserModal from "../../modals/EditUserModal";
import AssignFeedsModal from "../../modals/AssignFeedsModal";
import { ReactComponent as EmptyUsers } from "../../../assets/placeholders/emptyLeads.svg";
import { SEARCH_DEBOUNCE_DURATION } from "../../backend/consts";
import { WrapOverflow, AppBackgroundColor } from "../../styles";
import { UserRolesEnum } from "../../interfaces";
import { Trans } from "react-i18next";
import { Menu } from "../../components/Menu/Menu";
import { MenuSection } from "../../components/Menu/Menu";
import { MenuButton } from "../../components/Menu/Menu";

const getAccountUser = (accountUsers: any[], accountId: any) => {
  return find(accountUsers, { account: { id: accountId } });
};

export const DeleteButton = styled(Button)`
  padding: 0 11px;
  max-width: 56px;
  min-height: 48px;
  min-width: unset;
  &:hover {
    background-color: ${({ theme }) => theme.colors.alert.base};
    fill: ${({ theme }) => theme.colors.white.base};
  }
  svg {
    width: 14px !important;
    height: 14px !important;
  }

  &:hover {
    path {
      fill: ${({ theme }) => theme.colors.white.base};
    }
  }
`;

const CustomButton = styled(Button)`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  padding: 0 25px;
  width: 46px;
  height: 44px;

  svg {
    width: 20px !important;
    height: 20px !important;
  }
`;

const ListItemCellFirst = styled(ListItemCell)`
  padding-left: 30px;
`;

const ListItemCellLast = styled(ListItemCell)`
  padding-right: 30px;
`;

const ActiveMenuButton = styled(MenuButton)`
  padding: 8px 16px 8px 16px;
  font-size: 13px;
  font-weight: 400;
`;

const UserStatusLabel = styled(Block)<{ status: any }>`
  color: ${({ theme, status }) =>
    status === "Pending"
      ? theme.colors.accent.darkest
      : theme.colors.success.base};
`;

const UserRoles: FunctionComponent<any> = ({ user, account }) => {
  const roles = user.isInvite ? user.role : getCurrentRoles(user, account);
  return roles.map((role: any) => (
    <>
      {" "}
      {role.name}
      {isEqual(last(roles), role) ? "" : ", "}{" "}
    </>
  ));
};

const formatStatus = (str: string) => {
  if (!str) {
    return str;
  }

  return str.charAt(0).toUpperCase() + str.toLowerCase().slice(1);
};

const AccountUsers: FunctionComponent<IRouted> = ({
  t,
  slug,
  loading,
  setLoading,
  client,
  setTitle,
  user: currentUser,
  account,
  setError,
  setCtaConfig,
}) => {
  const [isOpen, toggleModal] = useToggle(false);
  const [userToDelete, setUserToDelete] = useState<any>();
  const [userToEdit, setUserToEdit] = useState<boolean>();
  const [userToAssignFeeds, setUserToAssignFeeds] = useState<any>();
  const [users, setUsers] = useState<any[]>();
  const [searchValue, setSearchValue] = useState<string>();

  useEffect(() => {
    setTitle(t("accounts.account_settings_label"));
  }, [setTitle, t]);

  useEffect(() => {
    loadData(searchValue);
  }, [client, searchValue]);

  const loadData = async (query?: string) => {
    setLoading(true);
    let _users: any[] = [];
    let _invites: any[] = [];

    try {
      _users = await client.Users.getUsers(
        undefined,
        undefined,
        "firstName",
        query,
        "asc"
      ).then(({ data }) => data);
    } catch (error) {
      _users = [];
      setError({
        error: true,
        title: "An issue loading users",
        errorResponse: error,
      });
    }

    try {
      _invites = await client.Invites.getInvites().then(({ data }) =>
        data.map((invite) => ({ ...invite, isInvite: true }))
      );
    } catch (error) {
      setError({
        error: true,
        title: "An issue loading invites",
        errorResponse: error,
      });
      _invites = [];
    }

    setUsers([..._users, ..._invites]);
    setLoading(false);
    if (!account.account.is_lead) {
      setCtaConfig({
        label: t("accounts.invite_user"),
        action: toggleModal,
      });
    }

    return Promise.resolve();
  };

  // @TODO Check on roles to enforce at least one owner.
  const disableEditAndDelete = (_user: any) => {
    if (!users) return false;
    const userRoles = getCurrentRoles(_user, account);
    const isAccountOwner = userRoles.find(
      (role: any) => role.slug === UserRolesEnum.ROLE_OWNER
    );
    const accountOwners = users
      .filter((__user: any) => !__user.isInvite)
      .filter((__user: any) => {
        const _userRoles = getCurrentRoles(__user, account);
        const _isAccountOwner = _userRoles.find(
          (role: any) => role.slug === UserRolesEnum.ROLE_OWNER
        );
        return _isAccountOwner;
      });

    switch (true) {
      // The account has only one active user
      case size(users.filter((__user: any) => !__user.isInvite)) === 1:
        return true;

      // Is current logged in user
      case _user.email === currentUser.email:
        return true;

      // User is the only ACCOUNT_OWNER in the account
      case !!(isAccountOwner && size(accountOwners) === 1):
        return true;
      default:
        return false;
    }
  };
  const submitSearchValue = debounce(
    (value: string) => setSearchValue(value),
    SEARCH_DEBOUNCE_DURATION
  );

  if (!users) return null;

  return (
    <Layout>
      <AppBackgroundColor />

      <Block justifySelf="stretch">
        <Search
          attachedDown
          placeholder="Search users by name, email or role ... "
          pulledUp
          onChange={({ target: { value } }) => submitSearchValue(value)}
        />

        {/* <CtaButton label={t('Invite user')} onClick={() => toggleModal(true)} /> */}

        <Container skipContentPadding margin={{ top: 15 }}>
          <Paper scrollY={true}>
            {!loading && !searchValue && !users.length && (
              <EmptyTable
                title={t("Users")}
                description={t("It seems you have no users yet!")}
                image={<EmptyUsers />}
              />
            )}

            <List responsiveAt="small" condense={76}>
              {searchValue && (
                <EmptyEntityFallbackListItem
                  collection={users}
                  description={t("Oops, no results found!")}
                />
              )}

              {users.map((user, index) => (
                <ListItemBody key={index}>
                  <ListItemRow>
                    <ListItemCellFirst width="90px">
                      <Avatar user={user} avatarSize="medium" />
                    </ListItemCellFirst>
                    <ListItemCell width="225px">
                      <ListItemContent
                        label={`${t("Name")}:`}
                        description={name(user)}
                      />
                    </ListItemCell>
                    <ListItemCell width="225px">
                      <ListItemContent
                        label={`${t("Email")}:`}
                        description={
                          <WrapOverflow title={user.email}>
                            {user.email}
                          </WrapOverflow>
                        }
                      />
                    </ListItemCell>
                    <ListItemCell width="100px">
                      <ListItemContent
                        label={`${t("Status")}:`}
                        description={
                          <UserStatusLabel status={formatStatus(user.status)}>
                            {formatStatus(user.status) || t("Active")}
                          </UserStatusLabel>
                        }
                      />
                    </ListItemCell>
                    <ListItemCell width="100px">
                      <ListItemContent
                        label={`${t("Last Login")}:`}
                        description={
                          <WrapOverflow title={user.email}>
                            {(user.last_login_tim &&
                              user.last_login_tim &&
                              dt(user.last_login_time)) ||
                              "-"}{" "}
                          </WrapOverflow>
                        }
                      />
                    </ListItemCell>
                    <ListItemCell width="370px">
                      <ListItemContent
                        label={`${t("Roles")}:`}
                        description={
                          <UserRoles user={user} account={account} />
                        }
                      />
                    </ListItemCell>
                    <ListItemCellLast>
                      {!disableEditAndDelete(user) && (
                        <Block
                          display="grid"
                          direction="column"
                          gridGap={10}
                          justifyContent="flex-end"
                        >
                          <Menu
                            withPortal={true}
                            menuButton={
                              <CustomButton
                                elementHeightBase={false}
                                elementWidth="block"
                                icon="menu"
                                buttonType="outline"
                              />
                            }
                          >
                            <MenuSection>
                              <ActiveMenuButton
                                withIcon
                                color="gray"
                                shade="darkest"
                                elementWidth="block"
                                onClick={() => setUserToDelete(user)}
                              >
                                Delete user
                              </ActiveMenuButton>
                              <ActiveMenuButton
                                withIcon
                                color="gray"
                                shade="darkest"
                                elementWidth="block"
                                disablePointer={user.isInvite}
                                opacity={!user.isInvite ? 1 : 0.5}
                                onClick={() => setUserToEdit(user)}
                              >
                                Edit role
                              </ActiveMenuButton>
                              {getCurrentRoles(user, account).some(
                                (role: any) =>
                                  [
                                    UserRolesEnum.ROLE_OWNER,
                                    UserRolesEnum.ROLE_MANAGER,
                                    UserRolesEnum.ROLE_CURATOR,
                                  ].includes(role.slug)
                              ) && (
                                <ActiveMenuButton
                                  withIcon
                                  color="gray"
                                  shade="darkest"
                                  elementWidth="block"
                                  disablePointer={user.isInvite}
                                  opacity={!user.isInvite ? 1 : 0.5}
                                  onClick={() => {
                                    if (!user.isInvite)
                                      setUserToAssignFeeds(user);
                                  }}
                                >
                                  Assign Feeds
                                </ActiveMenuButton>
                              )}
                            </MenuSection>
                          </Menu>
                        </Block>
                      )}
                    </ListItemCellLast>
                  </ListItemRow>
                </ListItemBody>
              ))}
            </List>
          </Paper>
        </Container>
      </Block>

      {/* <Pagination meta={data.meta} onNavigate={async (page) => await loadData(page, searchValue)} /> */}

      <InviteUserModal
        skipDefaultHeight
        isOpen={isOpen}
        users={users}
        onSubmit={() => {
          toggleModal(false);
          loadData();
        }}
        onCancel={() => toggleModal(false)}
      />

      {userToEdit && (
        <EditUserModal
          skipDefaultHeight
          user={userToEdit}
          isOpen={!!userToEdit}
          onSubmit={() => {
            window.location.reload();
          }}
          onCancel={() => setUserToEdit(false)}
        />
      )}

      {userToAssignFeeds && (
        <AssignFeedsModal
          skipDefaultHeight
          user={userToAssignFeeds}
          isOpen={!!userToAssignFeeds}
          onSubmit={() => {
            setUserToAssignFeeds(undefined);
            loadData(searchValue);
          }}
          onCancel={() => setUserToAssignFeeds(undefined)}
        />
      )}

      {userToDelete && (
        <ConfirmationModal
          isOpen={!!userToDelete}
          onConfirm={() => {
            if (userToDelete.first_name) {
              return client.Users.deleteUser(
                getAccountUser(userToDelete.account_users, account.account.id)
                  .id
              )
                .then(() => loadData())
                .then(() => {
                  setUserToDelete(null);
                });
            } else {
              return client.Invites.deleteInvite(userToDelete.id).then(() => {
                setUserToDelete(null);
                loadData();
              });
            }
          }}
          onCancel={() => setUserToDelete(null)}
          icon="deleteSign"
          title=" "
          confirmLabel={t("accounts.delete_user_button_remove_user")}
          subTitle={t("accounts.delete_user_confirmation_title")}
          description={<Trans i18nKey="accounts.delete_user_confirmation" />}
        />
      )}
    </Layout>
  );
};

export { AccountUsers };
